import type { ReactNode } from 'react'
import { AlertCircle, Inbox, RotateCcw } from 'lucide-react'
import { Spinner } from './Spinner'

/**
 * Cele trei stari pe care le are orice lista inainte sa aiba randuri: se incarca, a dat eroare,
 * e goala. Din 136 de fisiere cu interogari, 111 nu verificau eroarea — un API picat arata
 * „Se incarca..." la nesfarsit sau un tabel gol, adica „nu exista date", ceea ce e fals.
 *
 *   <StareLista interogare={q} gol={!q.data?.length} mesajGol="Niciun decont in luna aceasta.">
 *     <table>…</table>
 *   </StareLista>
 */
export function StareLista({
  interogare,
  gol,
  mesajGol,
  children,
}: {
  interogare: {
    isLoading: boolean
    isError: boolean
    error?: unknown
    refetch: () => unknown
    isFetching?: boolean
  }
  gol: boolean
  /** O propozitie care spune de ce e goala si, daca se poate, ce faci ca sa nu mai fie. */
  mesajGol: ReactNode
  children: ReactNode
}) {
  if (interogare.isLoading)
    return (
      <div className="flex justify-center py-10">
        <Spinner label="Se încarcă…" />
      </div>
    )

  if (interogare.isError)
    return (
      <div
        role="alert"
        className="mx-auto my-6 flex max-w-md flex-col items-center gap-2 rounded-caseta border border-b4l-red/30 bg-b4l-red-light px-5 py-4 text-center"
      >
        <AlertCircle size={20} className="text-b4l-red" />
        <p className="text-corp font-medium text-b4l-red-dark">Datele nu s-au putut încărca.</p>
        <p className="text-mic text-b4l-red-dark/80">{mesajEroare(interogare.error)}</p>
        <button
          type="button"
          onClick={() => interogare.refetch()}
          disabled={interogare.isFetching}
          className="mt-1 inline-flex items-center gap-1.5 rounded-caseta border border-b4l-red/40 bg-b4l-surface px-3 py-1.5 text-mic font-medium text-b4l-red-dark hover:bg-b4l-red-light disabled:opacity-60"
        >
          <RotateCcw size={13} className={interogare.isFetching ? 'animate-spin' : ''} />
          Reîncearcă
        </button>
      </div>
    )

  if (gol)
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center text-corp text-b4l-text-muted">
        <Inbox size={22} className="text-b4l-text-dim" />
        {mesajGol}
      </div>
    )

  return <>{children}</>
}

/** Mesajul trimis de API (`{ error: { message } }`), altfel unul general. */
function mesajEroare(e: unknown): string {
  const m = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message
  if (m) return m
  if ((e as { response?: unknown })?.response === undefined)
    return 'Serverul nu răspunde. Verifică conexiunea și reîncearcă.'
  return 'Eroare neașteptată. Dacă se repetă, spune-i unui admin.'
}
